export interface RandomResult<T> {
  value: T;
  step: number;
}

function hashStep(seed: number, step: number): number {
  let value = (seed + step * 0x6d2b79f5) >>> 0;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
}

export function randomAt(seed: number, step: number): RandomResult<number> {
  return { value: hashStep(seed, step), step: step + 1 };
}

export function randomInt(
  seed: number,
  step: number,
  minInclusive: number,
  maxExclusive: number
): RandomResult<number> {
  if (maxExclusive <= minInclusive) {
    throw new Error("maxExclusive must be greater than minInclusive");
  }
  const result = randomAt(seed, step);
  return {
    value: Math.floor(result.value * (maxExclusive - minInclusive)) + minInclusive,
    step: result.step
  };
}

export function pickOne<T>(
  seed: number,
  step: number,
  values: readonly T[]
): RandomResult<T> {
  if (values.length === 0) {
    throw new Error("Cannot pick from an empty collection.");
  }
  const index = randomInt(seed, step, 0, values.length);
  const value = values[index.value];
  if (value === undefined) {
    throw new Error("Random selection returned an invalid index.");
  }
  return { value, step: index.step };
}

export function weightedPick<T>(
  seed: number,
  step: number,
  values: readonly T[],
  getWeight: (item: T) => number
): RandomResult<T> {
  if (values.length === 0) {
    throw new Error("Cannot pick from an empty collection.");
  }
  const total = values.reduce((sum, item) => sum + Math.max(0, getWeight(item)), 0);
  if (total <= 0) {
    return pickOne(seed, step, values);
  }
  const result = randomAt(seed, step);
  let cursor = result.value * total;
  for (const item of values) {
    cursor -= Math.max(0, getWeight(item));
    if (cursor <= 0) return { value: item, step: result.step };
  }
  const fallback = values[values.length - 1];
  if (fallback === undefined) throw new Error("Weighted selection failed.");
  return { value: fallback, step: result.step };
}
